import React, { createContext, useContext, useEffect, useState } from "react";
import { translations, languages } from "@/lib/i18n";

const LanguageContext = createContext(null);

const RTL = new Set(["ar", "he", "fa", "ur"]);

// Map our internal codes → Google Translate codes (mostly identical; zh needs mapping)
const GT_MAP = {
  en: "en", hi: "hi", mr: "mr", ta: "ta", te: "te", bn: "bn",
  gu: "gu", kn: "kn", ml: "ml", pa: "pa", or: "or",
  es: "es", fr: "fr", de: "de", ar: "ar", pt: "pt", ja: "ja",
  zh: "zh-CN",
};

/**
 * Set the googtrans cookies so Google Translate picks up the target language.
 * Cookies must be set on the host + all parent domains for the widget to read.
 */
function setGTCookies(target) {
  const host = window.location.hostname;
  const val = target === "en" ? "" : `/en/${target}`;
  const expires = target === "en"
    ? "expires=Thu, 01 Jan 1970 00:00:00 GMT;"
    : "";
  const parts = host.split(".");
  // Set on current + parent domain (e.g. example.com and .example.com)
  const domains = new Set([
    host,
    parts.length > 1 ? "." + parts.slice(-2).join(".") : host,
  ]);
  for (const d of domains) {
    document.cookie = `googtrans=${val}; path=/; domain=${d}; ${expires}`;
  }
  document.cookie = `googtrans=${val}; path=/; ${expires}`;
}

/**
 * Trigger Google Translate widget without a page reload by driving the hidden
 * <select> that the widget injects. Falls back to a reload if the select
 * isn't ready yet (e.g. on first paint before the script loads).
 */
function triggerGoogleTranslate(target) {
  const gtCode = GT_MAP[target] || target;
  setGTCookies(gtCode);

  const drive = () => {
    const sel = document.querySelector(".goog-te-combo");
    if (sel) {
      sel.value = gtCode;
      sel.dispatchEvent(new Event("change"));
      return true;
    }
    return false;
  };

  if (!drive()) {
    // Widget not ready yet — poll a few times, then fallback to reload
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (drive() || tries > 20) {
        clearInterval(iv);
        if (tries > 20 && target !== "en") window.location.reload();
      }
    }, 150);
  }
}

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("bs_lang") || "en");

  // On mount & on every lang change: update <html>, dir, and drive Google Translate.
  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", RTL.has(lang) ? "rtl" : "ltr");
    triggerGoogleTranslate(lang);
  }, [lang]);

  const changeLang = (code) => {
    setLang(code);
    localStorage.setItem("bs_lang", code);
  };

  const t = (key) => {
    const dict = translations[lang] || translations.en;
    return dict[key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ lang, changeLang, t, languages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
